import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {ApiConfig, Channel, ChannelConfig, LookupMediaData, Step,} from '../../../shared/models/cerebrum-core';
import {ChannelKeywords, FileNode, StageRequest} from '../../../shared/models/flow-manager-core';
import {isNullOrUndefined} from 'util';
import {environment} from '../../../../environments/environment';
import {Router} from '@angular/router';
import {map, shareReplay, tap} from 'rxjs/operators';
import {FlowMetaData} from '../../../shared/models/flow-meta-data';
import find from 'lodash/find';
import {FlowUsageResponse} from '../../../shared/models/FlowUsage';
import {ConfigModel} from '../../../shared/models/ConfigModel';
import {SharedService} from '../../../core/services/store/shared-store/shared-storage.service';

interface CachedImage {
  url: string;
  blob: Blob;
}

interface Flow_Response {
  result: boolean,
  message: string,
  flowMetaData?: any
}

@Injectable()
export class FlowManagerService {


  private static readonly baseUri: string = environment.controlBaseUrl;
  private static readonly baseApiUri: string = environment.controlBaseUrl + environment.flowManagerBaseUri;
  private static readonly baseApiUriV2: string = environment.controlBaseUrl + environment.flowManagerBaseUriV2;
  private static readonly baseApiUriV3: string = environment.controlBaseUrl + environment.flowManagerBaseUri + '/v3';
  private availableChannelsCache$: Map<string, Observable<Object>>;
  flowNameCache$: Observable<FlowMetaData[]>;
  flowMetaCache$: Observable<FlowMetaData>;
  flows$: Observable<Map<string, string>>;
  clientConfig$: Observable<ConfigModel>;
  idpListCash$: Observable<any>;
  integrationListCash$: Observable<any>;
  whatsappContentCache$: Observable<any>;
  channelCash$: Observable<any>;

  // i Don't want to do this.
  // keeping track of the env and the clientId
  // when client or env change means that the cache sould also change
  flowNameEnvCache = '';
  flowNameClientIdCache = '';
  utils: any;

  flowsCache$: Observable<Array<any>>;
  private _cacheUrls: string[] = [];
  private _cachedImages: CachedImage[] = [];

  private globalEnvironment;

  constructor(
    private http: HttpClient,
    private router: Router,
    private sharedService: SharedService
  ) {}

  getFlows(reload: boolean = false, botId: string = ''): Observable<Array<any>> {
    if (this.isReloadRequired(reload)) {
      this.sharedService.setFlowsUpdated(true);
      this.flowsCache$ = this.retrieveFlows().pipe(shareReplay(1));
    }
    if(botId && botId !== '') {
      return this.flowsCache$.pipe(
        map(arr =>
          arr.filter( r => r.id === botId )
        )
      );
    }
    return this.flowsCache$;
  }

  retrieveFlows(): Observable<Array<any>> {
    return this.http.get<Array<FileNode>>(this.createEntityUriV3([environment.flowsUri]));
  }

  lookUp(reference): Observable<LookupMediaData> {
    return this.http.get<LookupMediaData>(this.createEntityUriV2(['lookup', reference]));
  }

  getApis(): Observable<Map<string, ApiConfig>> {
    return this.http.get<Map<string, ApiConfig>>(
      this.createEntityUri([environment.endpointsUri])
    );
  }

  getSandboxApisForProd(): Observable<Map<string, ApiConfig>> {
    let finalUrl = FlowManagerService.baseApiUri;
    const baseUri = ['dev', this.sharedService.getEntityType(), this.sharedService.getEntityId()];
    const newPath = baseUri.concat([environment.endpointsUri]);

    newPath.forEach((path) => {
      finalUrl = finalUrl.concat('/').concat(path);
    });
    return this.http.get<Map<string, ApiConfig>>(finalUrl);
  }

  getChannels(env: string): Observable<Array<Channel>> {
    return this.http.get<Array<Channel>>(
      this.createEntityUriCustomEnv([environment.channelsUri], env)
    );
  }

  getChannelsById(channelId: string): Observable<Channel> {
    return this.http.get<Channel>(this.createEntityUri([environment.channelsUri, channelId])).pipe(
      map((channel) => {
        switch (channel.config['channel-name']) {
          case 'ussd':
            channel.config['channel-name'] = 'USSD';
            return channel;
          case 'sms':
            channel.config['channel-name'] = 'SMS';
            return channel;
          case 'whatsApp':
            channel.config['channel-name'] = 'WhatsApp';
            return channel;
          case 'abc':
            channel.config['channel-name'] = 'ABC';
            return channel;
          case 'webChat':
            channel.config['channel-name'] = 'WebChat';
            return channel;
          default:
            return channel;
        }
      })
    );
  }

  getFlow(flowId: string): Observable<Map<String, Step>> {
    return this.http.get<Map<String, Step>>(this.createEntityUriV3([environment.flowsUri, flowId]));
  }

  getFlowMetaData(reload: boolean = false): Observable<FlowMetaData[]> {
    if (this.isReloadRequired(reload)) {
      this.flowNameCache$ = this.retrieveFlowMetaData().pipe(
        shareReplay(1),
        tap((metaData) => this.sharedService.setMetaData(metaData))
      );
    }
    return this.flowNameCache$;
  }

  getFlows_with_ids(reload: boolean = false): Observable<any> {
    if (this.isReloadRequired(reload)) {
      this.flows$ = this.retrieveFlows_with_ids().pipe(shareReplay(1));
    }
    this.sharedService.setFlowsUpdated(false);
    return this.flows$;
  }

  clearFlowNameCache() {
    this.flowNameCache$ = null;
  }

  private retrieveFlowNames() {
    return this.getFlows(true).pipe(
      map((fileNodes: any[]) => this.deriveFlowName('', [], fileNodes))
    );
  }

  private retrieveFlows_with_ids() {
    return this.retrieveFlows().pipe(map((fileNodes: any[]) => this.deriveFlows('', {}, fileNodes)));
  }

  createFlow(stageFlowRequest: StageRequest): Observable<Flow_Response> {
    this.sharedService.setFlowsUpdated(true);
    return this.http.post<Flow_Response>(this.createEntityUriV3([environment.flowsUri, 'create']), stageFlowRequest);
  }

  saveFlow(stageFlowRequest: StageRequest): Observable<Flow_Response> {
    this.sharedService.setFlowsUpdated(true);
    return this.http
      .post<Flow_Response>(this.createEntityUriV3([environment.flowsUri, 'stage']), stageFlowRequest)
  }

  saveCopyOfFlow(copyFlow: any): Observable<boolean> {
    this.sharedService.setFlowsUpdated(true);
    return this.http
      .post<boolean>(this.createEntityUriV2([environment.flowUri, 'stage', 'copy']), copyFlow)
  }

  saveApis(flowName: string, stageFlowRequest: StageRequest): Observable<Boolean> {
    return this.http.post<Boolean>(
      this.createEntityUri([environment.endpointUri, 'stage', flowName]),
      stageFlowRequest
    );
  }

  saveRoutes(stageFlowRequest: StageRequest): Observable<Boolean> {
    return this.http.post<Boolean>(
      this.createEntityUri([environment.routeUri, 'stage']),
      stageFlowRequest
    );
  }

  private deriveFlowName(parent: string, names: string[], fileNodes: any[]): string[] {
    for (const fileNode of fileNodes) {
      const name = parent !== '' ? parent + '/' + fileNode.name : fileNode.name;
      const id = parent !== '' ? parent + '/' + fileNode.id : fileNode.name;
      // console.log('IA deriveFlowName ---> ', name);
      if (!isNullOrUndefined(fileNode.children)) {
        this.deriveFlowName(name, names, fileNode.children);
      } else {
        names.push(name);
      }
    }
    return names;
  }

  private deriveFlows(parent: string, flows: Object, fileNodes: any[]): any {
    for (const fileNode of fileNodes) {
      const name = parent !== '' ? parent + '/' + fileNode.name : fileNode.name;
      const id = fileNode.id;
      flows[id] = name;
      if (!isNullOrUndefined(fileNode.children)) {
        this.deriveFlows(name, flows, fileNode.children);
      }
    }
    return flows;
  }

  public getKeywordMap(channelId: string): Observable<Map<string, ChannelKeywords>> {
    return this.http.get<any>(this.createEntityUri(['keyword', channelId]));
  }

  public saveKeywords(channelKeywords: StageRequest) {
    return this.http.post<boolean>(this.createEntityUri(['keyword', 'stage']), channelKeywords);
  }

  public getFlowSteps(flowId: string) {
    return this.http.get<Array<string>>(this.createEntityUriV3([environment.flowsUri, flowId, 'steps']));
  }

  private createEntityUri(paths: string[]) {
    let finalUrl = FlowManagerService.baseApiUri;
    const baseUri = [
      this.sharedService.getEnvironment(),
      this.sharedService.getEntityType(),
      this.sharedService.getEntityId(),
    ];
    const newPath = baseUri.concat(paths);

    newPath.forEach((path) => {
      finalUrl = finalUrl.concat('/').concat(path);
    });
    return finalUrl;
  }

  private createEntityUriV2(paths: string[]) {
    let finalUrl = FlowManagerService.baseApiUriV2;
    const baseUri = [
      this.sharedService.getEnvironment(),
      this.sharedService.getEntityType(),
      this.sharedService.getEntityId()
    ];
    const newPath = baseUri.concat(paths);

    newPath.forEach((path) => {
      finalUrl = finalUrl.concat('/').concat(path);
    });
    return finalUrl;
  }

  private createEntityUriV3(paths: string[]) {
    let finalUrl = FlowManagerService.baseApiUriV3;
    const baseUri = [
      this.sharedService.getEnvironment(),
      this.sharedService.getEntityType(),
      this.sharedService.getEntityId()
    ];
    const newPath = baseUri.concat(paths);

    newPath.forEach((path) => {
      finalUrl = finalUrl.concat('/').concat(path);
    });
    return finalUrl;
  }

  private createEntityUriCustomEnv(paths: string[], env: string) {
    let finalUrl = FlowManagerService.baseApiUri;
    const baseUri = [env, this.sharedService.getEntityType(), this.sharedService.getEntityId()];
    const newPath = baseUri.concat(paths);

    newPath.forEach((path) => {
      finalUrl = finalUrl.concat('/').concat(path);
    });

    return finalUrl;
  }

  setSelectedRoute(currentRoute: string) {
    const DEFAULT_BTN_CLASS = 'w3-bar-item btn-nav';
    this.router.config[1].children[0].children.forEach((element) => {
      if (element.path === currentRoute) {
        document.getElementById(currentRoute).className = DEFAULT_BTN_CLASS + ' navSelected';
      } else {
        document.getElementById(element.path).className = DEFAULT_BTN_CLASS;
      }
    });
  }

  public getChannelConfig(channelId: string): Observable<ChannelConfig> {
    return this.http.get<ChannelConfig>(this.createEntityUri(['channelConfig', channelId]));
  }

  public getAllChannelConfig(): Observable<ChannelConfig[]> {
    return this.http.get<ChannelConfig[]>(this.createEntityUri(['channelConfig']));
  }

  public saveChannelConfig(
    stageRequest: StageRequest,
    env: string,
    clientId: string
  ): Observable<HttpResponse<Object>> {
    return this.http.post(
      this.createEntityUri([env, clientId, 'channel', 'saveConfig']),
      stageRequest,
      { observe: 'response' }
    );
  }

  public getAvaiableChannels(env: string, clientId: string) {
    if (!this.availableChannelsCache$) {
      this.availableChannelsCache$ = new Map<string, Observable<Object>>();
    }
    if (!this.availableChannelsCache$[clientId]) {
      this.availableChannelsCache$[clientId] = this.retrieveAvailableChannels(env, clientId);
    }

    return this.availableChannelsCache$[clientId];
  }

  public getClientConfig(): Observable<ConfigModel> {
    if (!this.clientConfig$) {
      this.clientConfig$ = this.http
        .get<ConfigModel>(this.createEntityUri(['config']))
        .pipe(shareReplay(1));
    }
    return this.clientConfig$;
  }

  public clearClientConfig(): void {
    this.clientConfig$ = null;
  }

  private isReloadRequired(isReloadRequired: boolean = false): boolean {
    if (
      isReloadRequired ||
      JSON.stringify(this.globalEnvironment) !==
        JSON.stringify(this.sharedService.getGlobalEnvironment())
    ) {
      this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
      return true;
    }
    return false;
  }

  private retrieveAvailableChannels(env: string, clientId: string) {
    return this.http.get(this.createEntityUri([env, clientId, 'channel', 'available']));
  }

  renameFlow(stageFlowRequest: any): Observable<Flow_Response> {
    this.sharedService.setFlowsUpdated(true);
    return this.http.put<Flow_Response>(
      this.createEntityUriV3([environment.flowsUri, stageFlowRequest.flowId, 'flowname']),
      stageFlowRequest
    )
  }

  moveFlow(stageFlowRequest: any): Observable<Flow_Response> {
    this.sharedService.setFlowsUpdated(true);
    return this.http.put<Flow_Response>(
      this.createEntityUriV3([environment.flowsUri, stageFlowRequest.flowId, 'flowpath']),
      stageFlowRequest
    );
  }

  private retrieveFlowMetaData(): Observable<FlowMetaData[]> {
    return this.http.get<FlowMetaData[]>(this.createEntityUriV3([environment.flowsUri, 'metadata']));
  }

  public getFlowMetaDataById(flowId: string): Observable<FlowMetaData> {
    return this.http.get<FlowMetaData>(this.createEntityUriV3([environment.flowsUri, flowId, 'metadata']));
  }

  public getFlowUsages(flowId: string) {
    return this.http.get<FlowUsageResponse>(this.createEntityUriV3([environment.flowsUri, flowId, 'usage']));
  }

  public deleteFlow(flowId: string): Observable<Flow_Response> {
    this.sharedService.setFlowsUpdated(true);
    return this.http.delete<Flow_Response>(this.createEntityUriV3([environment.flowsUri, flowId]));
  }

  getFlowMetaDataBy(description: any): Observable<FlowMetaData> {
    if (description) {
      const flowId = description.split(':')[0];
      if (!this.flowNameCache$) {
        this.getFlowMetaData(true);
      }

      return this.flowNameCache$.pipe(
        map((value: FlowMetaData[]) => {
          for (const x of value) {
            if (x.flowId === flowId) {
              return x;
            }
          }
        })
      );
    }
    return null;
  }

  getFlowByName(name) {
    let flow = null;
    if (this.flowNameCache$) {
      this.flowNameCache$.subscribe((data) => {
        flow = find(data, { flowId: name });
        flow = flow || find(data, {flowName: name});
      });
    }
    return flow;
  }

  set cacheUrls(url: string[]) {
    this._cacheUrls.push(url[0]);
  }

  get cacheUrls(): string[] {
    return this._cacheUrls;
  }

  set cachedImages(image: CachedImage) {
    this._cachedImages.push(image);
  }

  getImage(url: string) {

    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.send(null);
    request.onreadystatechange = () => {
      if (request.readyState === 4 && request.status === 200) {
        const type = request.getResponseHeader('Content-Type');
        if (type.indexOf('text') !== 1) {
          const svgContent = request.responseText;
          return this.checkAndCacheImage(url, svgContent);
        }
      }
    };
  }

  getIndex(url: string) {
    const index = this._cachedImages.findIndex((image) => image.url === url);

    if (index > -1) {
      const image = this._cachedImages[index];
      return image;
    }

   this.getImage(url);

  }

  checkAndCacheImage(url: string, blob) {
      this._cachedImages.push({ url, blob });
      return this.getIndex(url);
  }

  getChat2PayExtensions(): Observable<any> {
      let chat2payConfig = this.sharedService.getChat2PayExtensions();
      if (!chat2payConfig) {
          const uri = `${FlowManagerService.baseUri}/chat2pay/config/${this.sharedService.getEntityType()}/${this.sharedService.getEntityId()}`;
          return this.http.get(uri, {
            withCredentials: true,
            observe: 'response'
          }).pipe(
              tap(response => {
                chat2payConfig = of(response);
                this.sharedService.setChat2PayExtension(chat2payConfig);
              }));
        }
      return chat2payConfig;
  }

  getEntityURI(path) {
    return this.createEntityUri(path);
  }

  getIdpList(reload: boolean = false): Observable<any> {
    if (reload) {
      const uri = `${FlowManagerService.baseUri}/idp/${this.sharedService.getEnvironment()}/${this.sharedService.getEntityType()}/${this.sharedService.getEntityId()}/list`;
      return this.http.get(uri, {
        withCredentials: true,
        observe: 'response',
      }).pipe(
          tap(response => {
                this.idpListCash$ = of(response);
              }
          ));
    }
    return this.idpListCash$;
  }

  getIntDepDropdownList(reload: boolean, endpoint: string ): Observable<any> {
    if (reload) {
      const uri = `${FlowManagerService.baseUri}/chatdesk/${this.sharedService.getEnvironment()}/${this.sharedService.getEntityType()}/${this.sharedService.getEntityId()}/`+endpoint;
      return this.http.get(uri, {
        withCredentials: true,
        observe: 'response',
      }).pipe(
          tap(response => {
                this.integrationListCash$ = of(response);
              }
          ));
    }
    return this.integrationListCash$;
  }

    getChatDeskDepartments(): Observable<any> {
        return this.getChatDesk('departments')
    }

    getChatDeskIntegrations(): Observable<any> {
        return this.getChatDesk('integrations')
    }

   private getChatDesk(endpoint: string): Observable<any> {
        const uri = `${FlowManagerService.baseUri}/chatdesk/${this.sharedService.getEnvironment()}/${this.sharedService.getEntityType()}/${this.sharedService.getEntityId()}/`+endpoint;
        return this.http.get(uri)
    }

  getChannelList(reload: boolean = false): Observable<any> {
    if (reload) {
      const uri = `${FlowManagerService.baseUri}/api/${this.sharedService.getEnvironment()}/${this.sharedService.getEntityType()}/${this.sharedService.getEntityId()}/channelConfig`;
      return this.http.get(uri, {
        withCredentials: true,
        observe: 'response',
      }).pipe(
          tap(response => {
                this.channelCash$ = of(response);
              }
          ));
    }
    return this.channelCash$;
  }

  private error(message: string): Observable<Flow_Response> {
    return of({
      message,
      result: false
    });
  }
}

